// src/services/rule-engine/EffectManager.ts
// エフェクト管理システム

import { GameAction } from '../../types/editor/GameScript';
import { RuleExecutionContext, GameObject } from './types';

/**
 * エフェクト管理クラス
 * scale, flash, shake, rotate, particles等のエフェクトを管理
 */
export class EffectManager {
  constructor() {
    console.log('✨ EffectManager初期化');
  }

  /**
   * エフェクトアクションを実行
   */
  executeEffectAction(
    action: Extract<GameAction, { type: 'effect' }>,
    context: RuleExecutionContext
  ): void {
    const targetObj = context.objects.get(action.targetId);
    if (!targetObj) {
      return;
    }

    const effect = action.effect;
    const durationMs = (effect.duration || 0.2) * 1000;

    switch (effect.type) {
      case 'scale':
        this.executeScaleEffect(targetObj, effect, durationMs);
        break;
      case 'flash':
        this.executeFlashEffect(targetObj, effect, durationMs);
        break;
      case 'shake':
        this.executeShakeEffect(targetObj, effect, durationMs);
        break;
      case 'rotate':
        this.executeRotateEffect(targetObj, effect, durationMs);
        break;
      case 'particles':
        this.executeParticlesEffect(targetObj, effect, durationMs, context);
        break;
      default:
        break;
    }
  }

  /**
   * スケールエフェクト実行
   */
  private executeScaleEffect(targetObj: GameObject, effect: any, durationMs: number): void {
    const obj = targetObj as any;
    if (targetObj.baseScale === undefined) {
      targetObj.baseScale = targetObj.scale;
    }
    // Save scaleX/scaleY bases so rendering (which prefers scaleX ?? scale) can be updated
    if (obj.baseScaleX === undefined) obj.baseScaleX = obj.scaleX ?? targetObj.scale;
    if (obj.baseScaleY === undefined) obj.baseScaleY = obj.scaleY ?? targetObj.scale;

    // Save center position so scaling stays visually centered (not top-left anchored)
    targetObj.effectCenterX = targetObj.x + (targetObj.width * obj.baseScaleX) / 2;
    targetObj.effectCenterY = targetObj.y + (targetObj.height * obj.baseScaleY) / 2;

    const scaleAmount = effect.scaleAmount || 0.5;
    targetObj.effectScale = scaleAmount;
    targetObj.effectStartTime = performance.now();
    targetObj.effectDuration = durationMs;
    targetObj.effectType = 'scale';
  }

  /**
   * フラッシュエフェクト実行
   */
  private executeFlashEffect(targetObj: GameObject, effect: any, durationMs: number): void {
    if (targetObj.baseOpacity === undefined) {
      targetObj.baseOpacity = 1.0;
    }
    
    targetObj.effectType = 'flash';
    targetObj.effectStartTime = performance.now();
    targetObj.effectDuration = durationMs;
    targetObj.flashColor = effect.flashColor || '#FFFFFF';
    targetObj.flashIntensity = effect.flashIntensity || 0.5;
    targetObj.flashFrequency = effect.flashFrequency || 10;
  }

  /**
   * シェイクエフェクト実行
   */
  private executeShakeEffect(targetObj: GameObject, effect: any, durationMs: number): void {
    if (targetObj.originalX === undefined) {
      targetObj.originalX = targetObj.x;
    }
    if (targetObj.originalY === undefined) {
      targetObj.originalY = targetObj.y;
    }
    
    targetObj.effectType = 'shake';
    targetObj.effectStartTime = performance.now();
    targetObj.effectDuration = durationMs;
    targetObj.shakeIntensity = effect.shakeIntensity || 5;
    targetObj.shakeFrequency = effect.shakeFrequency || 20;
    targetObj.shakeDirection = effect.shakeDirection || 'both';
  }

  /**
   * 回転エフェクト実行
   */
  private executeRotateEffect(targetObj: GameObject, effect: any, durationMs: number): void {
    if (targetObj.baseRotation === undefined) {
      targetObj.baseRotation = targetObj.rotation || 0;
    }
    
    targetObj.effectType = 'rotate';
    targetObj.effectStartTime = performance.now();
    targetObj.effectDuration = durationMs;
    targetObj.rotationAmount = effect.rotationAmount || 360;
    targetObj.rotationDirection = effect.rotationDirection || 'clockwise';
  }

  /**
   * パーティクルエフェクト実行
   */
  private executeParticlesEffect(
    targetObj: GameObject, 
    effect: any, 
    durationMs: number, 
    context: RuleExecutionContext
  ): void {
    if (context.particleSystem) {
      const particleType = effect.particleType || 'star';
      const particleCount = effect.particleCount || 20;
      const colors = Array.isArray(effect.particleColor) 
        ? effect.particleColor 
        : effect.particleColor 
          ? [effect.particleColor] 
          : this.getDefaultParticleColors(particleType);
      
      context.particleSystem.emit({
        x: targetObj.x + targetObj.width / 2,
        y: targetObj.y + targetObj.height / 2,
        type: particleType,
        count: particleCount,
        size: effect.particleSize || 10,
        colors: colors,
        spread: effect.particleSpread || 100,
        speed: effect.particleSpeed || 200,
        gravity: effect.particleGravity !== false,
        duration: durationMs / 1000
      });
    }
  }

  /**
   * エフェクトを更新（フレームごと）
   */
  updateEffects(context: RuleExecutionContext): void {
    const now = performance.now();

    context.objects.forEach((obj) => {
      // フェード更新（show/hide の fadeIn/fadeOut）
      if (obj.fadeDirection !== undefined && obj.fadeStartTime !== undefined && obj.fadeDuration !== undefined) {
        const fadeElapsed = now - obj.fadeStartTime;
        const fadeProgress = Math.min(fadeElapsed / obj.fadeDuration, 1.0);

        if (obj.fadeDirection === 'in') {
          obj.alpha = fadeProgress;
        } else {
          obj.alpha = 1 - fadeProgress;
          if (fadeProgress >= 1.0) {
            obj.visible = false;
            obj.alpha = 1;
          }
        }

        if (fadeProgress >= 1.0) {
          obj.fadeDirection = undefined;
          obj.fadeStartTime = undefined;
          obj.fadeDuration = undefined;
        }
      }

      // 通常エフェクト更新
      if (!obj.effectType || !obj.effectStartTime) {
        return;
      }

      const elapsed = now - obj.effectStartTime;
      const progress = Math.min(elapsed / obj.effectDuration!, 1.0);

      if (progress >= 1.0) {
        this.endEffect(obj);
        return;
      }

      switch (obj.effectType) {
        case 'scale':
          this.updateScaleEffect(obj, progress);
          break;
        case 'flash':
          this.updateFlashEffect(obj, elapsed);
          break;
        case 'shake':
          this.updateShakeEffect(obj, elapsed);
          break;
        case 'rotate':
          this.updateRotateEffect(obj, progress);
          break;
      }
    });
  }

  /**
   * スケールエフェクト更新
   */
  private updateScaleEffect(obj: GameObject, progress: number): void {
    if (obj.baseScale !== undefined && obj.effectScale !== undefined) {
      const t = progress * 2;
      let factor: number;
      if (t < 1) {
        factor = 1.0 - (1.0 - obj.effectScale) * t;
      } else {
        factor = obj.effectScale + (1.0 - obj.effectScale) * (t - 1);
      }
      obj.scale = obj.baseScale * factor;
      // Also update scaleX/scaleY — rendering uses these preferentially
      const o = obj as any;
      if (o.baseScaleX !== undefined) o.scaleX = o.baseScaleX * factor;
      if (o.baseScaleY !== undefined) o.scaleY = o.baseScaleY * factor;
      // Reposition so the object scales around its center, not top-left corner
      if (obj.effectCenterX !== undefined && o.baseScaleX !== undefined) {
        obj.x = obj.effectCenterX - (obj.width * o.baseScaleX * factor) / 2;
      }
      if (obj.effectCenterY !== undefined && o.baseScaleY !== undefined) {
        obj.y = obj.effectCenterY - (obj.height * o.baseScaleY * factor) / 2;
      }
    }
  }

  /**
   * フラッシュエフェクト更新
   */
  private updateFlashEffect(obj: GameObject, elapsed: number): void {
    const frequency = obj.flashFrequency || 10;
    const intensity = obj.flashIntensity || 0.5;
    
    const phase = (elapsed / 1000) * frequency * Math.PI * 2;
    const flashAmount = (Math.sin(phase) + 1) / 2;
    
    obj.flashValue = flashAmount * intensity;
  }

  /**
   * シェイクエフェクト更新
   */
  private updateShakeEffect(obj: GameObject, _elapsed: number): void {
    const intensity = obj.shakeIntensity || 5;
    const direction = obj.shakeDirection || 'both';

    const shakeX = (Math.random() - 0.5) * 2 * intensity;
    const shakeY = (Math.random() - 0.5) * 2 * intensity;

    // 固定originalXではなく現在位置にオフセットを乗せる（moveアクションと共存）
    // shakeOffsetX/Yで前フレームのオフセットを打ち消してから新オフセットを加算
    const prevOffsetX = obj.shakeOffsetX ?? 0;
    const prevOffsetY = obj.shakeOffsetY ?? 0;

    if (direction === 'horizontal' || direction === 'both') {
      obj.x = obj.x - prevOffsetX + shakeX;
      obj.shakeOffsetX = shakeX;
    }
    if (direction === 'vertical' || direction === 'both') {
      obj.y = obj.y - prevOffsetY + shakeY;
      obj.shakeOffsetY = shakeY;
    }
  }

  /**
   * 回転エフェクト更新
   */
  private updateRotateEffect(obj: GameObject, progress: number): void {
    const amount = obj.rotationAmount || 360;
    const direction = obj.rotationDirection || 'clockwise';
    const multiplier = direction === 'clockwise' ? 1 : -1;
    
    const easedProgress = 1 - Math.pow(1 - progress, 3);
    
    obj.rotation = obj.baseRotation! + (amount * multiplier * easedProgress * Math.PI / 180);
  }

  /**
   * エフェクト終了処理
   */
  private endEffect(obj: GameObject): void {
    switch (obj.effectType) {
      case 'scale':
        if (obj.baseScale !== undefined) {
          obj.scale = obj.baseScale;
          const o = obj as any;
          if (o.baseScaleX !== undefined) o.scaleX = o.baseScaleX;
          if (o.baseScaleY !== undefined) o.scaleY = o.baseScaleY;
          // Restore position to center-anchored original
          if (obj.effectCenterX !== undefined && o.baseScaleX !== undefined) {
            obj.x = obj.effectCenterX - (obj.width * o.baseScaleX) / 2;
          }
          if (obj.effectCenterY !== undefined && o.baseScaleY !== undefined) {
            obj.y = obj.effectCenterY - (obj.height * o.baseScaleY) / 2;
          }
        }
        break;
      case 'flash':
        obj.flashValue = 0;
        if (obj.baseOpacity !== undefined) {
          obj.alpha = obj.baseOpacity;
          obj.baseOpacity = undefined;
        }
        break;
      case 'shake':
        // オフセットを打ち消して元の位置に戻す
        obj.x = obj.x - (obj.shakeOffsetX ?? 0);
        obj.y = obj.y - (obj.shakeOffsetY ?? 0);
        obj.shakeOffsetX = undefined;
        obj.shakeOffsetY = undefined;
        break;
      case 'rotate':
        if (obj.baseRotation !== undefined) {
          obj.rotation = obj.baseRotation;
        }
        break;
    }
    
    obj.effectType = undefined;
    obj.effectStartTime = undefined;
    obj.effectDuration = undefined;
  }

  /**
   * ゲームリセット時にすべてのオブジェクトのエフェクト状態をクリア
   */
  reset(context: RuleExecutionContext): void {
    context.objects.forEach((obj) => {
      obj.effectType = undefined;
      obj.effectStartTime = undefined;
      obj.effectDuration = undefined;
      obj.effectScale = undefined;
      obj.baseScale = undefined;
      (obj as any).baseScaleX = undefined;
      (obj as any).baseScaleY = undefined;
      obj.effectCenterX = undefined;
      obj.effectCenterY = undefined;
      obj.flashValue = undefined;
      obj.flashColor = undefined;
      obj.flashIntensity = undefined;
      obj.flashFrequency = undefined;
      obj.baseOpacity = undefined;
      obj.shakeOffsetX = undefined;
      obj.shakeOffsetY = undefined;
      obj.shakeIntensity = undefined;
      obj.shakeFrequency = undefined;
      obj.shakeDirection = undefined;
      obj.baseRotation = undefined;
      obj.rotationAmount = undefined;
      obj.rotationDirection = undefined;
    });
  }

  /**
   * パーティクルタイプのデフォルト色を取得
   */
  private getDefaultParticleColors(type: string): string[] {
    switch (type) {
      case 'star':
        return ['#FFD700', '#FFA500', '#FFFF00'];
      case 'confetti':
        return ['#FF0000', '#00FF00', '#0000FF', '#FFFF00', '#FF00FF', '#00FFFF'];
      case 'explosion':
        return ['#FF4500', '#FF6347', '#FFA500', '#FFD700'];
      case 'splash':
        return ['#1E90FF', '#00BFFF', '#87CEEB'];
      case 'hearts':
        return ['#FF1493', '#FF69B4', '#FFB6C1'];
      case 'sparkle':
        return ['#FFFFFF', '#F0F8FF', '#E6E6FA'];
      default:
        return ['#FFFFFF'];
    }
  }
}